import { type FC, type KeyboardEvent, useMemo, useState } from 'react'
import { type SerializedError } from '@reduxjs/toolkit'
import { type FetchBaseQueryError } from '@reduxjs/toolkit/query'
import classNames from 'classnames'
import { Modal } from '../../../../components/Modal/Modal'
import { Button } from '../../../../components/Button/Button'
import {
  CustomDropdownItemOption,
  type DropDownItemOptionComponentProps,
  SelectDropdown,
  type SelectOption,
} from '../../../../components/SelectDropdown/SelectDropdown'
import { isFetchBaseQueryError } from '../../../../models/ApiErrors'
import { type SmsAreaCode } from '../../../../models/Scheduling'
import { areaCodeLocation } from './areaCodeLocation'

interface ChangeSmsNumberModalProps {
  areaCodes: SmsAreaCode[] | undefined
  isLoadingAreaCodes: boolean
  isAreaCodesError: boolean
  isSaving: boolean
  error: FetchBaseQueryError | SerializedError | undefined
  requestedAreaCode: string | null
  currentAreaCode: string | null
  onRequest: (areaCode: string) => void
  onRetryAreaCodes: () => void
  onCancel: () => void
}

const toAreaCodeOption = (areaCode: SmsAreaCode, currentAreaCode: string | null): SelectOption => {
  const isCurrent = areaCode.areaCode === currentAreaCode
  const location = isCurrent ? `${areaCodeLocation(areaCode)} · Current area code` : areaCodeLocation(areaCode)

  return {
    id: areaCode.areaCode,
    label: `${areaCode.areaCode} · ${location}`,
    disabled: isCurrent,
    displayLabel: (
      <>
        <span className={classNames({ bold: !isCurrent })}>{areaCode.areaCode}</span>
        <span>{location}</span>
      </>
    ),
  }
}

const matchesSearch = (areaCode: SmsAreaCode, search: string): boolean =>
  `${areaCode.areaCode} ${areaCodeLocation(areaCode)} ${areaCode.stateName}`.toLowerCase().includes(search)

const selectOnEnterOrSpace = (event: KeyboardEvent<HTMLDivElement>) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    event.currentTarget.click()
  }
}

const AreaCodeDropdownItemOption: FC<DropDownItemOptionComponentProps> = ({ optionLabel, displayLabel, disabled, onClick }) => (
  <CustomDropdownItemOption
    role='button'
    tabIndex={disabled === true ? -1 : 0}
    aria-disabled={disabled}
    onClick={disabled === true ? undefined : onClick}
    onKeyDown={disabled === true ? undefined : selectOnEnterOrSpace}
    className={classNames('d-flex align-items-center gap-2 paragraph-medium py-2', { 'zocks-text-gray-500': disabled })}
  >
    {displayLabel ?? optionLabel}
  </CustomDropdownItemOption>
)

export const ChangeSmsNumberModal: FC<ChangeSmsNumberModalProps> = ({
  areaCodes,
  isLoadingAreaCodes,
  isAreaCodesError,
  isSaving,
  error,
  requestedAreaCode,
  currentAreaCode,
  onRequest,
  onRetryAreaCodes,
  onCancel,
}) => {
  const [search, setSearch] = useState('')
  const [selectedAreaCode, setSelectedAreaCode] = useState<string | null>(null)

  const options = useMemo(
    () => (areaCodes ?? []).filter((areaCode) => matchesSearch(areaCode, search)).map((areaCode) => toAreaCodeOption(areaCode, currentAreaCode)),
    [areaCodes, search, currentAreaCode],
  )

  const hasFailed = error != null && selectedAreaCode != null && selectedAreaCode === requestedAreaCode
  const isNotAvailable = hasFailed && isFetchBaseQueryError(error) && error.status === 404
  const primaryLabel = isSaving ? 'Changing number' : isNotAvailable ? 'Try again' : 'Change number'

  const handleSearchChange = async (input: string) => {
    setSearch(input.trim().toLowerCase())
    setSelectedAreaCode(null)
  }

  const handleAreaCodeSelected = (option: SelectOption) => {
    setSelectedAreaCode(option.id)
    setSearch('')
  }

  const handleCancel = () => {
    if (!isSaving) {
      onCancel()
    }
  }

  const handleRequest = () => {
    if (selectedAreaCode != null) {
      onRequest(selectedAreaCode)
    }
  }

  return (
    <Modal
      show
      centered
      footerClassName='p-4 pt-0'
      onHide={handleCancel}
      header={<h5 className='mb-0'>Change sending number</h5>}
      headerBodySeparator
      body={
        <div className='d-flex flex-column gap-4'>
          <div className='paragraph-medium'>
            Choose the area code clients will see. If a number with that area code is ready, texts switch to it right away. If not, we&apos;ll set one up first.
          </div>
          <div className='d-flex flex-column gap-2'>
            <span className='bold paragraph-small'>Area code</span>
            <SelectDropdown
              options={options}
              initialSelectedOption={null}
              placeholder='Search by area code, city or state'
              className={classNames({ 'border-danger': hasFailed })}
              onOptionSelected={handleAreaCodeSelected}
              onSearchInputChange={handleSearchChange}
              DropDownItemOptionComponent={AreaCodeDropdownItemOption}
              loading={isLoadingAreaCodes}
              disabled={isAreaCodesError || isSaving}
              ariaLabel='Area code'
              searchable
              hideClearButton
            />
            {isAreaCodesError ? (
              <span className='paragraph-small zocks-text-danger'>
                Couldn&apos;t load the area codes.{' '}
                <Button className='d-inline p-0 align-baseline zocks-text-primary text-decoration-underline' onClick={onRetryAreaCodes}>
                  Try again
                </Button>
              </span>
            ) : null}
            {hasFailed ? (
              <span className='paragraph-small zocks-text-danger'>
                {isNotAvailable
                  ? `We couldn't get a number with area code ${selectedAreaCode} right now. Try again later or choose another area code.`
                  : "We couldn't request the number. Try again."}
              </span>
            ) : null}
          </div>
          <div className='d-flex align-items-start gap-2 p-3 rounded-2 zocks-bg-color-gray-100 paragraph-small zocks-text-gray-700'>
            <i aria-hidden='true' className='bi bi-info-circle flex-shrink-0' />
            <span>Until the new number is ready, texts keep going out from your current number.</span>
          </div>
        </div>
      }
      footer={
        <div className='d-flex zocks-gap-0.75 justify-content-end w-100'>
          <Button variant='secondary' disabled={isSaving} onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant='primary' loading={isSaving} disabled={selectedAreaCode == null} onClick={handleRequest}>
            {primaryLabel}
          </Button>
        </div>
      }
    />
  )
}
